import axios from 'axios';
import { API_BASE_URL } from '@/api/config';

/**
 * Per-request opt-out of the 401 refresh/retry/redirect machinery, carried on
 * the axios config rather than as a header.
 *
 * It used to travel as `X-Skip-Auth-Retry`. That was a purely client-side
 * signal riding on a custom header, which in production put it in the CORS
 * preflight: the SPA (Static Web App origin) and the API (App Service origin)
 * are cross-origin, the API's allowedHeaders list did not include it, and the
 * browser blocked the EDC SSO exchange outright — the handoff then fell back
 * to the login page as if the token had been rejected. A config flag never
 * reaches the wire, so it cannot be blocked.
 */
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRetry?: boolean;
  }
}

// ─── In-memory token store ──────────────────────────────────────────────────

let _accessToken: string | null = null;

/** Called by AuthContext to set/clear the in-memory access token. */
export function setAuthToken(token: string | null): void {
  _accessToken = token;
}

/** Read the current in-memory access token (used by interceptors). */
export function getAuthToken(): string | null {
  return _accessToken;
}

// ─── Axios instance ─────────────────────────────────────────────────────────

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30_000,
});

// ─── Request interceptor: attach auth token + request ID ────────────────────

client.interceptors.request.use(
  (config) => {
    // Attach access token from in-memory store (NOT localStorage)
    if (_accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${_accessToken}`;
    }

    // Add X-Request-ID for distributed tracing
    if (config.headers) {
      config.headers['X-Request-ID'] = crypto.randomUUID();
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ─── Response interceptor: handle 401 + normalise errors ────────────────────

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
}

/**
 * A refresh-token callback injected by AuthContext so the client can
 * transparently retry 401'd requests without circular imports.
 */
let _refreshCallback: (() => Promise<string | null>) | null = null;

export function setRefreshCallback(cb: (() => Promise<string | null>) | null): void {
  _refreshCallback = cb;
}

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error);

    const originalRequest = error.config as typeof error.config & { _retry?: boolean };
    const status = error.response?.status;

    // Opt out of the refresh/retry/redirect machinery. Set by callers that
    // establish a session themselves (the EDC SSO exchange) — a 401 there
    // means "this handoff token is bad", not "the CTMS session expired", and
    // bouncing to /login mid-flight would strand the caller's own fallback.
    // The legacy header is still honoured so a cached older bundle keeps
    // working against this client.
    const skipAuthRetry =
      Boolean(originalRequest?.skipAuthRetry) ||
      Boolean(originalRequest?.headers?.['X-Skip-Auth-Retry']);

    // Handle 401 - attempt token refresh
    if (status === 401 && originalRequest && !originalRequest._retry && !skipAuthRetry) {
      if (isRefreshing) {
        // Queue this request until the refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(client(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;

      if (_refreshCallback) {
        isRefreshing = true;
        try {
          const newToken = await _refreshCallback();
          if (newToken) {
            _accessToken = newToken;
            processQueue(null, newToken);
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return client(originalRequest);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Redirect to login
          _accessToken = null;
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // No refresh callback or refresh returned null - redirect to login
      _accessToken = null;
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Surface backend message when available
    const message =
      error.response?.data?.message ??
      error.response?.data?.error ??
      error.message ??
      'An unexpected error occurred';

    return Promise.reject(new Error(message));
  },
);

export default client;
